import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import type { PortalContext, ShellUser } from '../../lib/navigation.js';

/**
 * useQueryClient() throws when no provider is mounted. Production always
 * has one (routes/__root.tsx), but unit tests render shell components in
 * isolation — return null there so logout still works (minus cache clear).
 * The hook call itself stays unconditional, so hook order is stable.
 */
function useOptionalQueryClient() {
  try {
    return useQueryClient();
  } catch {
    return null;
  }
}

export interface AccountMenuProps {
  user: ShellUser | null;
  context: PortalContext;
  /** Compact mode hides the user name + role label, leaving just the avatar
   *  circle. Used inside the collapsed sidebar rail. */
  compact?: boolean;
}

function roleLabel(user: ShellUser | null): string {
  if (!user) return 'Pengunjung';
  if (user.role === 'admin') return 'Admin';
  if (user.role === 'tutor') return user.isApprovedTutor ? 'Tutor' : 'Calon tutor';
  if (user.kind === 'parent') return 'Orang tua';
  if (user.kind === 'student') return 'Siswa';
  return 'Pengunjung';
}

export function AccountMenu({ user, context, compact = false }: AccountMenuProps): ReactNode {
  const [isOpen, setIsOpen] = useState(false);
  const queryClient = useOptionalQueryClient();
  const initial = user?.name?.charAt(0).toUpperCase() ?? '?';
  const isAuthed = Boolean(user);
  const isPublic = context === 'public';
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    function onDocClick(event: MouseEvent) {
      if (!menuRef.current) return;
      if (!menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setIsOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  if (!isAuthed) {
    return (
      <div className="account-menu">
        <Link
          className="button button-secondary"
          to="/login"
          search={{ error: undefined, redirect: undefined }}
        >
          Masuk
        </Link>
      </div>
    );
  }

  return (
    <div
      className={`account-menu account-menu-authenticated ${compact ? 'account-menu-compact' : ''}`}
      ref={menuRef}
    >
      <button
        type="button"
        className="button button-ghost account-menu-trigger"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-label="Buka menu akun"
        onClick={() => setIsOpen((value) => !value)}
      >
        <span aria-hidden="true" className="account-menu-avatar">
          {initial}
        </span>
        {!compact && (
          <span className="account-menu-identity">
            <span className="account-menu-name">{user?.name}</span>
            <span className="account-menu-role">{roleLabel(user)}</span>
          </span>
        )}
      </button>
      {isOpen && (
        <div className="account-menu-panel" role="menu">
          <div>
            <div className="account-menu-name">{user?.name}</div>
            <div className="account-menu-role">{user?.email}</div>
          </div>
          <ul className="account-menu-list">
            {isPublic ? (
              <li>
                <Link
                  to="/login"
                  search={{ error: undefined, redirect: undefined }}
                  onClick={() => setIsOpen(false)}
                >
                  Masuk
                </Link>
              </li>
            ) : (
              <li>
                <button
                  type="button"
                  className="account-menu-logout"
                  onClick={async () => {
                    setIsOpen(false);
                    try {
                      await fetch('/api/auth/logout', {
                        method: 'POST',
                        credentials: 'include',
                      });
                    } catch {
                      // Ignore network errors — we still want to clear the
                      // local session and redirect home.
                    }
                    // Drop cached session/progress so the next visitor never
                    // sees the previous user's dashboard (BUG-SIS-05).
                    queryClient?.clear();
                    window.location.assign('/');
                  }}
                >
                  Keluar
                </button>
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
